import type {
  BankAccountMasked,
  CustomerAddress,
  CustomerConsent,
  CustomerProfile,
  CustomerDocumentView,
  PaginatedData,
} from '@solar/contracts';

import { apiClient } from '../../lib/api-client';
import { toQuery } from '../../lib/api-query';

export interface CustomerListFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
}

export const customersApi = {
  getProfile(): Promise<CustomerProfile> {
    return apiClient.get('/customer/profile');
  },

  createProfile(input: {
    firstName: string;
    middleName?: string;
    lastName: string;
    phoneNumber: string;
    alternatePhone?: string;
    dateOfBirth?: string;
    occupation?: string;
    employerName?: string;
  }): Promise<CustomerProfile> {
    return apiClient.post('/customer/profile', input);
  },

  updateProfile(input: {
    firstName?: string;
    middleName?: string;
    lastName?: string;
    phoneNumber?: string;
    alternatePhone?: string;
    dateOfBirth?: string;
    occupation?: string;
    employerName?: string;
  }): Promise<CustomerProfile> {
    return apiClient.patch('/customer/profile', input);
  },

  listAddresses(): Promise<CustomerAddress[]> {
    return apiClient.get('/customer/addresses');
  },

  addAddress(input: {
    addressType: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    localGovernmentArea?: string;
    stateCode: string;
    countryCode?: string;
    postalCode?: string;
    isDefault?: 'true' | 'false';
  }): Promise<CustomerAddress> {
    return apiClient.post('/customer/addresses', input);
  },

  updateAddress(
    id: string,
    input: {
      addressLine1?: string;
      addressLine2?: string;
      city?: string;
      localGovernmentArea?: string;
      stateCode?: string;
      postalCode?: string;
      isDefault?: 'true' | 'false';
    },
  ): Promise<CustomerAddress> {
    return apiClient.patch(`/customer/addresses/${id}`, input);
  },

  listConsents(): Promise<CustomerConsent[]> {
    return apiClient.get('/customer/consents');
  },

  recordConsent(input: {
    consentType: string;
    documentVersion?: string;
  }): Promise<CustomerConsent> {
    return apiClient.post('/customer/consents', input);
  },

  listDocuments(): Promise<CustomerDocumentView[]> {
    return apiClient.get('/customer/documents');
  },

  uploadNin(file: File): Promise<CustomerDocumentView> {
    const body = new FormData();
    body.append('file', file);
    return apiClient.postForm('/customer/documents/nin', body);
  },

  uploadSignature(file: File): Promise<CustomerDocumentView> {
    const body = new FormData();
    body.append('file', file);
    return apiClient.postForm('/customer/documents/signatures', body);
  },

  previewDocument(id: string, admin = false): Promise<Blob> {
    return apiClient.getBlob(
      admin ? `/admin/customer-documents/${id}/preview` : `/customer/documents/${id}/preview`,
    );
  },

  downloadDocument(id: string, admin = false): Promise<Blob> {
    return apiClient.getBlob(
      admin ? `/admin/customer-documents/${id}/download` : `/customer/documents/${id}/download`,
    );
  },

  listAdminDocuments(customerId: string): Promise<CustomerDocumentView[]> {
    return apiClient.get(`/admin/customers/${customerId}/documents`);
  },

  reviewAdminDocument(
    id: string,
    input: { status: 'VERIFIED' | 'REJECTED'; reason?: string },
  ): Promise<CustomerDocumentView> {
    return apiClient.patch(`/admin/customer-documents/${id}/review`, input);
  },

  listBankAccounts(): Promise<BankAccountMasked[]> {
    return apiClient.get('/customer/bank-accounts');
  },

  addBankAccount(input: {
    bankCode: string;
    bankNameSnapshot?: string;
    accountName: string;
    accountNumber: string;
    isDefault?: 'true' | 'false';
  }): Promise<BankAccountMasked> {
    return apiClient.post('/customer/bank-accounts', input);
  },

  updateBankAccount(
    id: string,
    input: { accountName?: string; isDefault?: 'true' | 'false' },
  ): Promise<BankAccountMasked> {
    return apiClient.patch(`/customer/bank-accounts/${id}`, input);
  },

  deactivateBankAccount(id: string, reason: string): Promise<BankAccountMasked> {
    return apiClient.post(`/customer/bank-accounts/${id}/deactivate`, { reason });
  },

  listAdmin(filters: CustomerListFilters = {}): Promise<PaginatedData<CustomerProfile>> {
    return apiClient.get(`/admin/customers${toQuery({ ...filters })}`);
  },

  getAdmin(id: string): Promise<CustomerProfile> {
    return apiClient.get(`/admin/customers/${id}`);
  },

  updateAdmin(
    id: string,
    input: {
      firstName?: string;
      lastName?: string;
      phoneNumber?: string;
      status?: string;
      onboardingStage?: string;
    },
  ): Promise<CustomerProfile> {
    return apiClient.patch(`/admin/customers/${id}`, input);
  },

  listAdminAddresses(customerId: string): Promise<CustomerAddress[]> {
    return apiClient.get(`/admin/customers/${customerId}/addresses`);
  },

  listAdminConsents(customerId: string): Promise<CustomerConsent[]> {
    return apiClient.get(`/admin/customers/${customerId}/consents`);
  },

  listAdminBankAccounts(customerId: string): Promise<BankAccountMasked[]> {
    return apiClient.get(`/admin/customers/${customerId}/bank-accounts`);
  },
};
